// Pristilbud på en ordre eller et skiplukk, rett fra ordren (OrderDetails, vinnsel 30.10), som på
// tekstiltrykk (TrykkTilbud.jsx). «Pris til kunden» (Order.prislinjer, OrdrePris.jsx) er tilbudet,
// så prisene registreres ett sted:
// - «Se tilbudet slik kunden ser det» viser linjene slik de er lagret på ordren (ForhandsvisTilbud).
// - «Send pristilbud» lager tilbudet (eller oppdaterer det kunden ikke har svart på) og sender det på
//   SMS og/eller e-post (POST /offers/fra-ordre/:id, POST /offers/:id/send-email).
// - Når prisene lagres, får et tilbud kunden ikke har svart på, de nye linjene (PATCH /orders/:id).
// Tilbudet kobles med Offer.ordreId, og statusen speiles på ordren (Order.tilbud). Når kunden har
// godkjent, sendes skiene til service («Gjør om til service»), eller kunden får beskjed med
// SMS-knappen øverst på ordren. («Klar for henting»-SMS-en herfra er fjernet, vinnsel 30.10.)
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import { feltKlasse, knappPrimar, knappSekundar } from '../ny-kundesamhandling/Skjema';
import Ikon from '../ny-kundesamhandling/Ikon';
import { statusFor, oppsummering, kr } from './kalkyle';
import formatDateTime from '../hjelpere/formatDateTime.js';
import { meldSmsSendt } from '../utils/smsStatus';
import ForhandsvisTilbud from './ForhandsvisTilbud';
import { linjeNokkel } from '../trykk/linjer';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const datoFelt = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');
const omDager = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return datoFelt(d); };

export default function OrdreTilbud({ ordre, kunde, klubb = null, onTilbud }) {
  const [offer, setOffer] = useState(null);
  const [utloper, setUtloper] = useState(omDager(14));
  const [kommentar, setKommentar] = useState('');
  const [sms, setSms] = useState(true);
  const [epost, setEpost] = useState(true);
  const [sender, setSender] = useState(false);
  const [forhandsvis, setForhandsvis] = useState(false);
  const [status, setStatus] = useState('');
  const [feil, setFeil] = useState('');
  const skiplukk = ordre.type === 'skiplukk';
  const linjer = (Array.isArray(ordre.prislinjer) ? ordre.prislinjer : [])
    .map((l) => ({ ...l, rabattKr: l.rabattKr ?? l.totalRabattKr ?? 0 }));
  const tilbudId = ordre.tilbud?.id || null;
  const tilbudStatus = ordre.tilbud?.status || null;

  // Tilbudet hentes på nytt når ordren får et nytt tilbud eller prisene er lagret.
  const lagret = JSON.stringify(linjer.map(linjeNokkel));
  useEffect(() => {
    if (!tilbudId) { setOffer(null); return undefined; }
    let avbrutt = false;
    fetch(`${API_BASE_URL}/offers/${tilbudId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((o) => {
        if (avbrutt || !o) return;
        setOffer(o);
        if (o.utloper && new Date(o.utloper) > new Date()) setUtloper(datoFelt(o.utloper));
        // «Skipakke etter skiplukk nr. N.» ble lagt inn automatisk før 30.10; feltet starter tomt da.
        setKommentar(/^Skipakke etter skiplukk nr\. \d+\.?$/.test((o.kommentar || '').trim()) ? '' : o.kommentar || '');
      })
      .catch(() => {});
    return () => { avbrutt = true; };
  }, [tilbudId, tilbudStatus, lagret]);

  const aktivt = offer && !['avslatt', 'kansellert', 'erstattet'].includes(offer.status);
  const godkjent = offer?.status === 'godkjent';
  const sendt = offer?.status === 'sendt';
  const st = offer ? statusFor(offer.status) : null;
  const avsluttet = ordre.Status === 'Avsluttet';
  const harTelefon = !!kunde?.phoneNumber;
  const harEpost = !!kunde?.email;
  const logg = offer?.statusLogs || [];
  const sistSendt = logg.filter((l) => l.status === 'sendt' && /sendt/i.test(l.comment || '')).map((l) => l.at).pop();
  const sistOppdatert = logg.filter((l) => /Linjene oppdatert fra ordren/.test(l.comment || '')).map((l) => l.at).pop();
  const oppdatertEtterSending = sendt && sistOppdatert && (!sistSendt || new Date(sistOppdatert) > new Date(sistSendt));
  // Tilbudet har andre linjer enn ordren (f.eks. endret i tilbudsmodulen eller laget før 30.10).
  const avvik = aktivt && !godkjent
    && JSON.stringify((offer.produkter || []).map(linjeNokkel)) !== JSON.stringify(linjer.map(linjeNokkel));
  const total = oppsummering(linjer).total;
  const ting = skiplukk ? 'skipakken' : 'varen';

  const send = async () => {
    setSender(true);
    setFeil('');
    setStatus('');
    try {
      const ansatt = Cookies.get('selectedEmployee') || '';
      const r = await fetch(`${API_BASE_URL}/offers/fra-ordre/${ordre._id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ansatt, utloper, kommentar, sendSms: sms && harTelefon }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.message || 'Klarte ikke å sende pristilbudet.');
      let o = b.offer;
      const deler = [];
      if (b.sms === 'sendt') { deler.push('på SMS'); meldSmsSendt(); }
      if (b.sms === 'feil') deler.push('(SMS-en feilet)');
      if (epost && harEpost) {
        const e = await fetch(`${API_BASE_URL}/offers/${o._id}/send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ansatt }),
        });
        deler.push(e.ok ? 'på e-post' : '(e-posten feilet)');
        if (e.ok) {
          const oo = await fetch(`${API_BASE_URL}/offers/${o._id}`).then((x) => (x.ok ? x.json() : null)).catch(() => null);
          if (oo) o = oo;
        }
      }
      setOffer(o);
      onTilbud({ id: o._id, status: o.status, dato: new Date().toISOString() });
      setStatus(deler.some((d) => d.startsWith('på')) ? `Pristilbudet er sendt ${deler.join(' og ')}.` : `Pristilbudet er lagret, men ikke sendt ${deler.join(' ')}`.trim());
    } catch (e) {
      setFeil(e.message);
    } finally {
      setSender(false);
    }
  };

  const knappTekst = !aktivt ? 'Send pristilbud' : (oppdatertEtterSending || avvik) && sendt ? 'Send oppdatert pristilbud' : sendt ? 'Send på nytt' : 'Send pristilbud';

  return (
    <section aria-labelledby="ot-tilbud" className="space-y-2 rounded-2xl border-2 border-amber-200 bg-amber-50 p-3 sm:p-3">
      <h2 id="ot-tilbud" className="flex flex-wrap items-center gap-2 text-base font-bold text-slate-900">
        {klubb ? 'Pristilbud til klubb' : 'Pristilbud'}
        {klubb?.klubbNavn && <span className="rounded-full bg-violet-700 px-2.5 py-0.5 text-sm font-semibold text-white [overflow-wrap:anywhere]">{klubb.klubbNavn}</span>}
        {st && <span className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${st.farge}`}>{st.navn}</span>}
      </h2>

      {godkjent ? (
        <p className="text-slate-800">
          Kunden har godkjent tilbudet{offer.quote?.approved?.at ? ` ${formatDateTime(offer.quote.approved.at)}` : ''}.
          {skiplukk ? ' Send skiene til service, eller gi kunden beskjed med SMS-knappen øverst.' : ' Gi kunden beskjed med SMS-knappen øverst når varen kan hentes.'}
        </p>
      ) : !linjer.length ? (
        <p className="text-slate-800">
          Er ikke prisen avtalt? Sett prisen på {ting} i «Pris til kunden» over. Den blir linjene i pristilbudet, og kunden godkjenner på lenken.
        </p>
      ) : (
        <>
          <p className="text-slate-800">
            {!aktivt
              ? `«Pris til kunden» over blir pristilbudet (${linjer.length} ${linjer.length === 1 ? 'linje' : 'linjer'}, ${kr(total)}). Kunden godkjenner på lenken.`
              : sendt
                ? `Sendt${sistSendt ? ` ${formatDateTime(sistSendt)}` : ''}. Venter på svar fra kunden.`
                : 'Tilbudet er lagret, men ikke sendt.'}
          </p>
          {!aktivt && offer && (
            <p className="text-sm text-slate-700">
              Forrige tilbud ble {offer.status === 'avslatt' ? 'avslått av kunden' : 'trukket tilbake'}. Et nytt lages når du sender.
            </p>
          )}
          {oppdatertEtterSending && !avvik && (
            <p className="rounded-lg bg-white px-3 py-1.5 text-sm text-slate-800">
              Lenken kunden har fått, viser de nye prisene. Send på nytt hvis kunden skal få beskjed.
            </p>
          )}
          {avvik && (
            <p className="rounded-lg bg-white px-3 py-1.5 text-sm text-amber-900">
              {sendt ? 'Tilbudet kunden har fått, har andre linjer enn «Pris til kunden». Send oppdatert pristilbud, så får kunden en ny versjon med ny lenke; den gamle lenken kan ikke lenger godkjennes.' : 'Tilbudet har andre linjer enn «Pris til kunden». Send det, så får kunden prisene over.'}
            </p>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label htmlFor="ot-utloper" className="mb-1 block text-sm font-medium text-slate-800">Gyldig til</label>
              <input id="ot-utloper" type="date" value={utloper} min={omDager(0)} onChange={(e) => setUtloper(e.target.value)} className={feltKlasse} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="ot-kommentar" className="mb-1 block text-sm font-medium text-slate-800">Kommentar til kunden (valgfritt)</label>
              <textarea id="ot-kommentar" rows={2} maxLength={2000} value={kommentar} onChange={(e) => setKommentar(e.target.value)} className={feltKlasse} />
            </div>
          </div>
          <fieldset className="flex flex-wrap gap-x-4 gap-y-1">
            <legend className={klubb ? 'mb-1 w-full text-sm font-semibold text-slate-800' : 'sr-only'}>
              {klubb ? `Sendes til ${klubb.navn || 'kontaktpersonen'} i ${klubb.klubbNavn || 'klubben'}` : 'Send til kunden'}
            </legend>
            <label className="flex min-h-[2.25rem] items-center gap-2 text-slate-800">
              <input type="checkbox" className="h-5 w-5" checked={sms && harTelefon} disabled={!harTelefon} onChange={(e) => setSms(e.target.checked)} />
              SMS{!harTelefon ? ' (mangler telefon)' : ''}
            </label>
            <label className="flex min-h-[2.25rem] items-center gap-2 text-slate-800">
              <input type="checkbox" className="h-5 w-5" checked={epost && harEpost} disabled={!harEpost} onChange={(e) => setEpost(e.target.checked)} />
              E-post med PDF{!harEpost ? ' (mangler e-post)' : ''}
            </label>
          </fieldset>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={send} disabled={sender || avsluttet || !kunde || !((sms && harTelefon) || (epost && harEpost))} className={knappPrimar}>
              <Ikon navn="send" className="w-6 h-6" /> {sender ? 'Sender …' : `${knappTekst} (${kr(total)})`}
            </button>
            <button type="button" onClick={() => setForhandsvis(true)} className={knappSekundar}>
              <Ikon navn="dokument" className="w-5 h-5" /> Se tilbudet slik kunden ser det
            </button>
          </div>
        </>
      )}
      {offer && offer.status !== 'utkast' && (
        <p className="text-sm">
          <Link to={`/offer-details/${offer._id}`} className="font-semibold text-indigo-700 underline underline-offset-2">Åpne i tilbudsmodulen</Link>
        </p>
      )}
      {feil && <p role="alert" className="text-sm text-red-700">{feil}</p>}
      <p role="status" className="text-sm text-slate-800">{status}</p>

      <ForhandsvisTilbud
        apen={forhandsvis}
        onLukk={() => setForhandsvis(false)}
        offer={offer}
        skjema={{ linjer, utloper, kommentar, rabattVisning: 'prosent' }}
        fornavn={kunde?.firstName || ''}
      />
    </section>
  );
}
